/*
 * Flavor quiz: intro → 4 questions (auto-advance + back) → personalized
 * 4-product starter set with add-all-to-cart.
 * Instance-safe: initialises every [data-flavor-quiz] independently; each
 * reads its own catalog (real products, server-rendered) from an embedded
 * JSON script tag. Cart adds go through /cart/add.js and then announce via
 * the theme contract: pf:cart:updated + [data-cart-count-badge] hooks.
 * Without JS the server-rendered intro card and line links remain.
 */
(function () {
  'use strict';

  /*
   * Built-in copy. This is now a FALLBACK: the merchant-editable wording comes
   * from the theme settings "Flavor quiz" panel, emitted by
   * snippets/flavor-quiz-flow.liquid as [data-quiz-questions]. readQuestions()
   * below prefers that and drops back to this if it is missing or malformed, so
   * a bad settings edit degrades to shipped wording instead of an empty quiz.
   *
   * The `key` and each option's `v` are NOT copy — rec() and headline() score
   * against them. Anything that changes them changes the recommendations.
   */
  var QUESTIONS = [
    { key: 'cook', q: 'What kind of cook are you?', opts: [
      { v: 'everyday', e: '🍳', t: 'Everyday & easy', d: 'Weeknight dinners, fast' },
      { v: 'bold', e: '🔥', t: 'Bold & spicy', d: 'The hotter the better' },
      { v: 'gourmet', e: '🍄', t: 'Gourmet & umami', d: 'Deep, savory, layered' },
      { v: 'herby', e: '🌿', t: 'Fresh & herby', d: 'Bright, garden flavors' }
    ] },
    { key: 'heat', q: 'How much heat can you handle?', opts: [
      { v: 'none', e: '😌', t: 'Keep it mellow', d: 'No heat for me' },
      { v: 'little', e: '🌶️', t: 'A little kick', d: 'Warm, not wild' },
      { v: 'burn', e: '🥵', t: 'Bring the burn', d: 'I like to sweat' },
      { v: 'melt', e: '☠️', t: 'Melt my face', d: 'Maximum heat' }
    ] },
    { key: 'cuisine', q: "What's your go-to cuisine?", opts: [
      { v: 'american', e: '🍽️', t: 'Everyday cooking', d: 'Weeknight meals, no fuss' },
      { v: 'italian', e: '🍝', t: 'Italian', d: 'Basil, oregano, garlic' },
      { v: 'mexican', e: '🌮', t: 'Mexican', d: 'Chiles, cilantro, smoke' },
      { v: 'asian', e: '🍜', t: 'Asian', d: 'Ginger, mushrooms, umami' },
      { v: 'bbq', e: '🍖', t: 'BBQ & grilling', d: 'Rubs, smoke, spice' }
    ] },
    { key: 'mood', q: 'Pick your kitchen mood', opts: [
      { v: 'classic', e: '⭐', t: 'Keep it classic', d: 'Reliable staples' },
      { v: 'mix', e: '🎨', t: 'Mix it up', d: 'A little of everything' },
      { v: 'surprise', e: '🎲', t: 'Surprise me', d: "Chef's choice" }
    ] }
  ];

  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function updateBadges(count) {
    document.querySelectorAll('[data-cart-count-badge]').forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  /*
   * Merchant copy from the theme settings, if it parses and looks sane.
   * "Sane" = every question keeps its key and at least one option, and every
   * option keeps a value — those are the parts the scoring depends on. A
   * question that fails the check falls back to the built-in one at the same
   * index, so one bad line cannot take the whole quiz down.
   */
  function readQuestions(root) {
    var el = root.querySelector('[data-quiz-questions]');
    if (!el) return QUESTIONS;
    var parsed;
    try { parsed = JSON.parse(el.textContent); } catch (e) { return QUESTIONS; }
    if (!Array.isArray(parsed) || parsed.length !== QUESTIONS.length) return QUESTIONS;
    return parsed.map(function (q, i) {
      var base = QUESTIONS[i];
      if (!q || q.key !== base.key || !Array.isArray(q.opts)) return base;
      var opts = q.opts.filter(function (o) { return o && o.v; });
      if (!opts.length) return base;
      return { key: base.key, q: q.q || base.q, opts: opts };
    });
  }

  function initQuiz(root) {
    if (root.dataset.quizInit) return;
    root.dataset.quizInit = '1';
    var S = root.querySelector('[data-quiz-stage]');
    // Rail hooks. Progress, Back and the recap live in the left column now
    // (reference/Flavor Quiz Page.dc.html), not inside the question card, so
    // they persist across renders instead of being rebuilt with each question.
    var RAIL = {
      meta: root.querySelector('[data-quiz-meta]'),
      count: root.querySelector('[data-quiz-count]'),
      segs: root.querySelector('[data-quiz-segs]'),
      back: root.querySelector('[data-quiz-back]'),
      recap: root.querySelector('[data-quiz-recap]')
    };
    // Set by the snippet so the question heading sits one level under the rail
    // heading — h2 under the page's h1, h3 under the homepage section's h2.
    var QLEVEL = root.dataset.quizQlevel === 'h2' ? 'h2' : 'h3';
    var catEl = root.querySelector('[data-quiz-catalog]');
    if (!S || !catEl) return;
    var QUESTIONS = readQuestions(root);
    var CATALOG;
    try { CATALOG = JSON.parse(catEl.textContent); } catch (e) { return; }
    if (!Object.keys(CATALOG).length) return;
    Object.keys(CATALOG).forEach(function (k) {
      // image_url emits protocol-relative URLs; anchor them to https so they
      // resolve off-domain too (the local dev proxy serves from 127.0.0.1)
      if (CATALOG[k].img && CATALOG[k].img.indexOf('//') === 0) {
        CATALOG[k].img = 'https:' + CATALOG[k].img;
      }
    });
    var cartUrl = root.dataset.cartUrl || '/cart';

    var ans = {};
    var step = 0;
    // The quiz is live from the first question. Q1 is server-rendered; wiring
    // it in place (rather than re-rendering) avoids a flash and a focus/aria
    // jump on page load. `started` gates heading focus so the first paint
    // never steals focus or scrolls the page to the quiz.
    var started = false;

    function focusHeading() {
      var h = S.querySelector('[data-quiz-focus]');
      if (h) h.focus();
    }

    function questionHTML(index) {
      var Q = QUESTIONS[index];
      return '<div class="quiz-ask">' +
        '<' + QLEVEL + ' class="quiz-q" tabindex="-1" data-quiz-focus>' + Q.q + '</' + QLEVEL + '>' +
        '<div class="quiz-opts">' + Q.opts.map(function (o) {
          return '<button type="button" class="quiz-opt' + (ans[Q.key] === o.v ? ' is-selected' : '') + '" data-v="' + o.v + '">' +
            '<span class="quiz-opt-emoji" aria-hidden="true">' + o.e + '</span>' +
            '<span class="quiz-opt-title">' + o.t + '</span>' +
            '<span class="quiz-opt-desc">' + o.d + '</span>' +
            '<span class="quiz-opt-check" aria-hidden="true"></span></button>';
        }).join('') + '</div>' +
      '</div>';
    }

    function wireQuestion() {
      var Q = QUESTIONS[step];
      S.querySelectorAll('.quiz-opt').forEach(function (b) {
        b.addEventListener('click', function () {
          ans[Q.key] = b.dataset.v;
          S.querySelectorAll('.quiz-opt').forEach(function (x) { x.classList.remove('is-selected'); });
          b.classList.add('is-selected');
          started = true;
          setTimeout(function () { step++; render(); }, 240);
        });
      });
    }

    /*
     * Rail state: count, the four segments, Back, and the recap of answers so
     * far. `done` is the results screen, where there is no question to be
     * partway through — the whole block hides and Retake is the way back.
     */
    // Short recap labels, as the comp uses. Keyed by question key, so a question
    // without one falls back to its full text rather than rendering blank.
    var RECAP_LABELS = { cook: 'Cook', heat: 'Heat', cuisine: 'Cuisine', mood: 'Mood' };

    function renderRail(done) {
      if (!RAIL.meta) return;
      RAIL.meta.hidden = !!done;
      if (done) return;

      if (RAIL.count) RAIL.count.textContent = 'Question ' + (step + 1) + ' of ' + QUESTIONS.length;
      if (RAIL.back) RAIL.back.hidden = step === 0;
      if (RAIL.segs) {
        var segs = RAIL.segs.children;
        for (var i = 0; i < segs.length; i++) {
          segs[i].className = i < step ? 'is-done' : (i === step ? 'is-current' : '');
        }
      }

      if (!RAIL.recap) return;
      // Answered questions only, and never the one being asked.
      var rows = [];
      for (var j = 0; j < step; j++) {
        var Q = QUESTIONS[j];
        var picked = Q.opts.filter(function (o) { return o.v === ans[Q.key]; })[0];
        if (picked) rows.push({ i: j, label: RECAP_LABELS[Q.key] || Q.q, value: picked.t });
      }
      RAIL.recap.hidden = rows.length === 0;
      RAIL.recap.innerHTML = rows.map(function (r) {
        return '<li><button type="button" class="quiz-recap__row" data-quiz-jump="' + r.i + '">' +
          '<span class="quiz-recap__label">' + r.label + '</span>' +
          '<span class="quiz-recap__value">' + r.value + '</span></button></li>';
      }).join('');
    }

    function render() {
      if (step >= QUESTIONS.length) return results();
      S.innerHTML = questionHTML(step);
      wireQuestion();
      renderRail(false);
      if (started) focusHeading();
    }

    // Wired ONCE — the rail persists across renders, so re-binding per question
    // (as the in-card button did) would stack listeners.
    if (RAIL.back) {
      RAIL.back.addEventListener('click', function () { started = true; step--; render(); });
    }
    if (RAIL.recap) {
      RAIL.recap.addEventListener('click', function (e) {
        var row = e.target.closest('[data-quiz-jump]');
        if (!row) return;
        started = true;
        step = parseInt(row.dataset.quizJump, 10);
        render();
      });
    }

    /*
     * Scores every catalog product against the answers, then picks four under
     * two hard constraints.
     *
     * Replaces a set of hardcoded per-answer lists that ended in a fixed pad
     * list, ['garlic','basil','porcini','ancho','chef','blz']. Because most
     * answer paths only produced two or three picks, that pad ran almost every
     * time, which is why one reviewer saw ShakerZ Garlic in 88% of results,
     * a Basil in 66%, and Porcini in 48% — including Basil for "Mexican" and
     * nothing Asian for "Asian". All 192 answer combinations collapsed to just
     * 47 distinct result sets.
     *
     * The two constraints are the other half of that report:
     *   flavor  — never the same flavor twice. ShakerZ Basil and BlastZ Basil
     *             share flavor 'basil', so the pair can no longer co-occur.
     *   line    — at most 2 from any one line (1 when the mood is "mix it up",
     *             which is what that answer should mean), so a set cannot be
     *             all ShakerZ and PepperZ with nothing else.
     */
    function rec() {
      var HEAT_WANT = { none: 0, little: 1, burn: 3, melt: 4 };
      var want = HEAT_WANT[ans.heat];

      var scored = Object.keys(CATALOG).map(function (k) {
        var p = CATALOG[k];
        var s = 0;

        // Eligibility is a flag, NOT a very negative score. A sentinel score
        // is fragile here: the mood bonuses below are added afterwards, so
        // `s = -1000` followed by `s += 3` climbs back over any threshold and
        // "no heat for me" quietly returns a ghost chile.
        var eligible = true;

        // Cuisine is the strongest signal — it is the mismatch people notice.
        if (ans.cuisine) {
          s += (p.cuisines || []).indexOf(ans.cuisine) !== -1 ? 12 : -8;
        }

        if (p.cook === ans.cook) s += 6;

        if (p.heat > 0) {
          // "No heat for me" means no chiles at all, not milder chiles.
          if (ans.heat === 'none') {
            eligible = false;
          } else if (want != null) {
            var d = Math.abs(p.heat - want);
            s += d === 0 ? 8 : d === 1 ? 4 : -2;
          }
        } else if (ans.heat === 'none') {
          s += 3;
        }

        // "Keep it classic" leans on the staples that suit any kitchen.
        if (ans.mood === 'classic' && (p.cuisines || []).indexOf('american') !== -1) s += 3;

        // "Surprise me" breaks ties differently each run — the only place any
        // randomness enters, so every other answer set is reproducible.
        if (ans.mood === 'surprise') s += Math.random() * 5;

        return { k: k, s: s, p: p, eligible: eligible };
      }).filter(function (x) { return x.eligible; });

      scored.sort(function (a, b) { return b.s - a.s || (a.k < b.k ? -1 : 1); });

      var out = [], flavors = {}, lines = {};

      // Tops up the current selection instead of starting over, so each
      // relaxation below keeps what the stricter pass already achieved. A
      // restarting version threw away the line spread it had just earned:
      // "mix it up" with no heat came back as two ShroomZ and two BlastZ when
      // three lines were available.
      function fill(pool, maxPerLine) {
        for (var i = 0; i < pool.length && out.length < 4; i++) {
          var it = pool[i];
          if (flavors[it.p.flavor]) continue;
          if ((lines[it.p.line] || 0) >= maxPerLine) continue;
          out.push(it.k);
          flavors[it.p.flavor] = true;
          lines[it.p.line] = (lines[it.p.line] || 0) + 1;
        }
      }

      /*
       * Cuisine fit is the OUTER constraint and line spread the inner one, not
       * the other way round. Getting that backwards is how an Italian set ended
       * up holding a chile: no chile is tagged italian, so a strict
       * one-per-line rule had to reach outside the cuisine to find a fourth
       * line. Four Italian products across three lines is the better answer.
       *
       * Each step relaxes exactly one thing, so the widest net is only cast
       * when the cuisine genuinely cannot fill four — e.g. "no heat" plus a
       * cuisine whose matches are mostly chiles.
       */
      var onCuisine = scored.filter(function (x) {
        return (x.p.cuisines || []).indexOf(ans.cuisine) !== -1;
      });

      fill(onCuisine, ans.mood === 'mix' ? 1 : 2);
      fill(onCuisine, 2);
      fill(onCuisine, 4);
      fill(scored, 2);
      fill(scored, 4);
      return out;
    }

    function headline() {
      var a = { none: 'mellow', little: 'balanced', burn: 'fiery', melt: 'fearless' }[ans.heat] || 'custom';
      var b = { everyday: 'everyday', bold: 'bold', gourmet: 'umami-rich', herby: 'fresh & herby' }[ans.cook] || '';
      return 'Your ' + a + ', ' + b + ' starter set';
    }

    function results() {
      renderRail(true);
      var keys = rec();
      var total = keys.reduce(function (s, k) { return s + CATALOG[k].cents; }, 0);
      S.innerHTML =
        '<div class="quiz-results-panel">' +
          '<p class="quiz-kicker">Your match</p>' +
          '<' + QLEVEL + ' class="quiz-title" tabindex="-1" data-quiz-focus>' + headline() + '</' + QLEVEL + '>' +
          '<p class="quiz-lead">Four flavors picked from your answers.</p>' +
          '<div class="quiz-results">' + keys.map(function (k) {
            var p = CATALOG[k];
            return '<a class="quiz-result quiz-result--' + p.family + '" href="' + p.url + '">' +
              '<span class="quiz-result-img">' + (p.img ? '<img src="' + p.img + '" alt="" loading="lazy">' : '') + '</span>' +
              '<span class="quiz-result-body">' +
                '<span class="quiz-result-line">' + p.line + '</span>' +
                '<span class="quiz-result-meta">' +
                  '<span class="quiz-result-name">' + p.name + '</span>' +
                  '<span class="quiz-result-price">' + p.price + '</span>' +
                '</span>' +
              '</span></a>';
          }).join('') + '</div>' +
          '<div class="quiz-total">' +
            '<span class="quiz-total__label">Starter set total</span>' +
            '<span class="quiz-total__value">' + money(total) + '</span>' +
          '</div>' +
          '<div class="quiz-acts">' +
            '<button type="button" class="pf-button pf-button--secondary quiz-retake"><span>Retake</span></button>' +
            '<button type="button" class="pf-button quiz-addall"><span>Add all to cart</span></button>' +
          '</div>' +
          '<p class="quiz-status" role="status" data-quiz-status></p>' +
        '</div>';
      S.querySelector('.quiz-retake').addEventListener('click', function () {
        ans = {};
        step = 0;
        started = true;
        render();
      });
      S.querySelector('.quiz-addall').addEventListener('click', function (e) {
        var btn = e.currentTarget;
        if (btn.disabled) return;
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
        var status = S.querySelector('[data-quiz-status]');
        var items = keys.map(function (k) { return { id: CATALOG[k].id, quantity: 1 }; });
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        })
          .then(function (r) {
            if (!r.ok) throw new Error('add failed');
            return fetch('/cart.js', { headers: { Accept: 'application/json' } });
          })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            updateBadges(cart.item_count);
            btn.removeAttribute('aria-busy');
            btn.querySelector('span').textContent = 'Added to cart';
            if (status) status.innerHTML = keys.length + ' items added. <a href="' + cartUrl + '">View cart</a>';
            document.dispatchEvent(new CustomEvent('pf:cart:updated', { detail: { source: 'flavor-quiz' } }));
          })
          .catch(function () {
            btn.disabled = false;
            btn.removeAttribute('aria-busy');
            if (status) status.textContent = 'Something went wrong. Please try again.';
          });
      });
      focusHeading();
    }

    // Wire the server-rendered first question in place — no re-render on load.
    wireQuestion();
  }

  function boot() {
    document.querySelectorAll('[data-flavor-quiz]').forEach(initQuiz);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
